// Dependency imports
import {
  Box,
  CircularProgress,
  CircularProgressProps,
  DialogContent,
  IconButton,
  Slider,
  Stack,
  Tooltip,
  tooltipClasses,
  TooltipProps
} from '@mui/material';
import { styled } from '@mui/material/styles';

import LinearProgress, {
  linearProgressClasses
} from '@mui/material/LinearProgress';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import { GridCellParams, GridColDef, GridRowId } from '@mui/x-data-grid';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import DeleteIcon from '@mui/icons-material/Delete';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import useVideo from 'hooks/video';
import { useVideoUpload } from 'hooks/config';
import { useSnack } from 'plugins/snack';
import { useDispatch } from 'react-redux';
import { setPreventNavigation } from 'store/preventNavigation/preventNavigationSlice';
import DataGridComponent from 'components/DataGrid';
import { Modal } from 'components';
import moment from 'moment';
import { secondsToHms } from 'utils';
import { AppDispatch, RootState, store } from 'store';
import { setCurrentCameraId, uploadVideos } from 'store/config/configSlice';

import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import PauseIcon from '@mui/icons-material/Pause';
import { OctagonAlertIcon, Plus } from 'lucide-react';
import SearchSelect from 'components/SearchSelect';
import {
  useDeleteConfigurationsCamera,
  useGetConfiguration,
  useGetFrameStatus,
  useListConfigurations,
  useUpdateConfigType
} from 'api/sdk';
import {
  setAnnotationData,
  setObjectMapping,
  setWorkflowConfig
} from 'store/annotations/annotationSlice';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  setConfiguration,
  setFilteredVideos
} from 'store/configuration/configurationSlice';
import EditName from 'pages/configuration/list/editName';
import { resetFilterModel } from 'store/pagination/paginationSlice';
import {
  completeUploadSequence,
  incrementPendingUploads,
  resetPendingUploads,
  startUploadSequence
} from 'store/train/videoUploadTrackingSlice';

const ActionToolTip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.black
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.black
  }
}));

const VideoStatus: React.FC<{
  status: string;
  uploadProgress?: number;
  fail_reason?: string;
  onCancelUpload?: any;
}> = ({ status, uploadProgress, fail_reason, onCancelUpload }) => {
  switch (status) {
    case 'uploading':
      return (
        <Stack direction="row" spacing={0} alignItems="center">
          <ActionToolTip title="Uploading...">
            <IconButton disableRipple style={{ color: 'black' }}>
              <CircularProgressWithIcon value={uploadProgress || 0} />
            </IconButton>
          </ActionToolTip>
          <ActionToolTip title="Cancel Upload">
            <IconButton
              onClick={e => {
                e.stopPropagation();
                onCancelUpload();
              }}
              disableRipple
              style={{ color: 'black' }}
            >
              <HighlightOffIcon />
            </IconButton>
          </ActionToolTip>
        </Stack>
      );
    case 'uploaded':
      return (
        <Stack direction={'column'}>
          <div>
            <ActionToolTip title={'Uploaded'}>
              <IconButton disableRipple style={{ color: 'blue' }}>
                <CloudDoneIcon />
              </IconButton>
            </ActionToolTip>
          </div>
        </Stack>
      );
    case 'failed':
      return (
        <Stack direction={'column'}>
          <div>
            <ActionToolTip
              title={
                fail_reason || 'Incomplete Upload/Impropriate video file format'
              }
            >
              <IconButton disableRipple style={{ color: 'red' }}>
                <CancelRoundedIcon />
              </IconButton>
            </ActionToolTip>
          </div>
        </Stack>
      );
    default:
      return null;
  }
};

const VideoAction: React.FC<any> = ({ onDelete, status }) => {
  const isButtonDisabled = status === 'uploading';

  return (
    <Stack direction={'column'}>
      <div>
        <ActionToolTip title={'Delete'}>
          <IconButton
            disabled={isButtonDisabled}
            onClick={e => {
              e.stopPropagation();
              if (!isButtonDisabled) {
                onDelete();
              }
            }}
            disableRipple
            style={{
              cursor: isButtonDisabled ? 'not-allowed' : 'pointer',
              color: isButtonDisabled ? 'gray' : 'inherit'
            }}
          >
            <DeleteIcon />
          </IconButton>
        </ActionToolTip>
      </div>
    </Stack>
  );
};

function CircularProgressWithIcon(
  props: CircularProgressProps & { value: number }
) {
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <CircularProgress variant="determinate" {...props} color="inherit" />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <FileUploadIcon />
      </Box>
    </Box>
  );
}

interface Status {
  frames_generated: boolean;
  details: {
    [key: string]: boolean;
  };
}

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 10,
  width: 400,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[200],
    ...theme.applyStyles('dark', {
      backgroundColor: theme.palette.grey[800]
    })
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: '#1a90ff',
    ...theme.applyStyles('dark', {
      backgroundColor: '#308fe8'
    })
  }
}));

const UploadTrainVideos = () => {
  const { cameras, isLoading, handleDelete, setError, refresh, cancelUpload } =
    useVideoUpload();
  const currentConfigId = useSelector(
    (state: RootState) => state.configuration.configuration
  );
  const { data: configurationData } = useGetConfiguration({
    config_id: currentConfigId || ''
  });

  const [isRowEditing, setIsRowEditing] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const { data: configs, isLoading: loadingConfigs } = useListConfigurations();
  const { mutateAsync: deleteCamera } = useDeleteConfigurationsCamera();
  const [selectedRows, setSelectedRows] = useState<GridRowId[]>([]);
  const [open, setOpen] = useState(false);
  const [openFramesProgress, setOpenFramesProgress] = useState<boolean>(false);
  const [rows, setRows] = useState<any>([]);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [isPause, setPause] = useState<boolean>(true);
  const [isPlay, setPlay] = useState<boolean>(false);
  const [videoName, setVideoName] = useState<string | null>('');
  const [getVideoCurrentTime, setVicdeoCurrentTime] = useState<number>(0);
  const { videoRef, seek, reset, loading: videoLoading } = useVideo();
  const [videoDuration, setVideoDuration] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [lastTime, setLastTime] = useState(0);
  const Snack = useSnack();
  const navigate = useNavigate();
  const { mutateAsync: updateConfigType } = useUpdateConfigType();
  const dispatch = useDispatch<AppDispatch>();
  const [isVideoUploading, setIsVideoUploading] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const snack = useSnack();
  const [stableDisabledState, setStableDisabledState] = useState(false);
  const { refetch: getFrameGenerationResult } = useGetFrameStatus(
    currentConfigId || ''
  );
  const uploadSequenceInProgress = useSelector(
    (state: RootState) => state.videoUploadTracking.uploadSequenceInProgress
  );
  const activeUploads =
    useSelector(
      (state: RootState) => state.videoUploadTracking.activeUploads
    ) || 0;

  useEffect(() => {
    const inProgress = uploadSequenceInProgress || activeUploads > 0;

    if (inProgress) {
      setStableDisabledState(true);
    } else {
      const timer = setTimeout(() => {
        setStableDisabledState(false);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [uploadSequenceInProgress, activeUploads]);

  const isAnyVideoUploading = useCallback(() => {
    console.log('rows', rows);
    return (
      stableDisabledState ||
      uploadSequenceInProgress ||
      activeUploads > 0 ||
      (rows &&
        rows.some(
          (video: any) => video.status === 'uploading' || !video.video_url
        ))
    );
  }, [stableDisabledState, uploadSequenceInProgress, activeUploads, rows]);

  useEffect(() => {
    if (cameras && cameras.length > 0) {
      const uploadingCount = cameras.filter(
        camera => camera.status === 'uploading'
      ).length;

      if (uploadingCount > 0) {
        if (activeUploads !== uploadingCount || !uploadSequenceInProgress) {
          console.log(
            'Syncing activeUploads with camera status:',
            uploadingCount
          );
          dispatch(resetPendingUploads());
          dispatch(incrementPendingUploads(uploadingCount));
          dispatch(startUploadSequence());
        }
      } else if (uploadingCount === 0 && activeUploads > 0) {
        console.log('Resetting activeUploads as no cameras are uploading');
        dispatch(resetPendingUploads());

        setTimeout(() => {
          if (
            cameras.filter(camera => camera.status === 'uploading').length === 0
          ) {
            dispatch(completeUploadSequence());
          }
        }, 300);
      }

      setRows(cameras);
      const isUploading = cameras.some(
        (item: any) => item.upload_progress < 100
      );
      setIsVideoUploading(isUploading);

      if (isUploading) {
        dispatch(setPreventNavigation(true));
      } else {
        dispatch(setPreventNavigation(false));
      }
    }
  }, [cameras, activeUploads, uploadSequenceInProgress]);

  useEffect(() => {
    if (rows?.length > 0) {
      const uploadedRows = rows
        .filter((row: any) => row.status === 'uploaded')
        .map((row: any) => row.id);
      setSelectedRows(uploadedRows);
    }
  }, [rows]);

  useEffect(() => {
    const uploadSequenceInProgress =
      store.getState().videoUploadTracking.uploadSequenceInProgress;

    if (cameras && cameras.length > 0 && !uploadSequenceInProgress) {
      // Find videos with failed status
      const failedVideos = cameras.filter(camera => camera.status === 'failed');

      if (failedVideos.length > 0) {
        console.log(
          `Found ${failedVideos.length} failed videos to clean up:`,
          failedVideos
        );

        // Delete failed videos one by one
        failedVideos.forEach(video => {
          console.log(`Auto-deleting failed video: ${video.id}`);
          deleteCamera({
            params: {
              camera_id: video.id
            }
          });
        });

        snack({
          message: 'Deleting Failed Videos',
          severity: 'info'
        });

        // Refresh after deletion
        setTimeout(() => {
          refresh();
        }, 500);
      }
    }
  }, [cameras, refresh]);

  useEffect(() => {
    const handleBeforeUnload = (event: any) => {
      if (isVideoUploading) {
        const confirmationMessage =
          'A video is currently being uploaded. If you leave or refresh the page, the upload will be lost. Are you sure you want to proceed?';
        event.returnValue = confirmationMessage; // Standard for most browsers
        return confirmationMessage; // Required for some browsers
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (isVideoUploading) {
        dispatch(setPreventNavigation(false));
      }
    };
  }, [isVideoUploading]);

  useEffect(() => {
    const checkBuffering = () => {
      const video = videoRef.current;
      if (video) {
        if (
          videoDuration !== 0 &&
          (video.currentTime === videoDuration ||
            video.currentTime > videoDuration)
        ) {
          setPlay(true);
          setPause(false);
        }

        if (video.currentTime > videoDuration) {
          setLoading(false);
        } else if (
          video.currentTime === lastTime &&
          !isPlay &&
          video.currentTime !== videoDuration
        ) {
          setLoading(true);
        } else {
          setLoading(false);
        }
        setLastTime(video.currentTime);
      }
    };

    const interval = setInterval(checkBuffering, 500);

    return () => {
      clearInterval(interval);
    };
  }, [lastTime]);

  const showDuplicateError = (message: string) => {
    snack({
      message: message,
      severity: 'error'
    });
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (currentConfigId === 'Select Configuration') {
      setError('Please select configuration');
      return;
    }

    if (fileInputRef.current) {
      // event listener before opening file dialog
      fileInputRef.current.onchange = (e: Event) => {
        const input = e.target as HTMLInputElement;
        if (input.files && input.files.length > 5) {
          snack({
            message: 'Maximum 5 videoes can be selected at once.',
            severity: 'error'
          });
          input.value = ''; // Clear selection
          return;
        }
        // Call your existing handleFileChange if within limit
        handleFileChange(e as unknown as React.ChangeEvent<HTMLInputElement>);
      };
      fileInputRef.current.click();
    }
    setError('');
    let files = event.target.files;

    if (!files) return;

    // Check for file limit
    if (files.length > 5) {
      setError('You can only select up to 5 videos at a time');
      event.target.value = '';
      return;
    }
    dispatch(
      uploadVideos({
        configuration_id: currentConfigId || '',
        files,
        showDuplicateError
      })
    );

    event.target.value = '';
  };
  const tempData = selectedRows;

  useEffect(() => {
    dispatch(setFilteredVideos(tempData));
  }, [selectedRows]);

  const handleSliderChange = (event: Event, value: number | number[]) => {
    const newTime = Array.isArray(value) ? value[0] : value;
    if (newTime === videoDuration) {
      setLoading(false);
    }
    if (videoRef.current) {
      videoRef.current.currentTime = newTime;
      setVicdeoCurrentTime(newTime);
    }
  };

  function videoCurrentTime(event: SyntheticEvent<HTMLVideoElement, Event>) {
    setVicdeoCurrentTime(event?.currentTarget?.currentTime);
  }

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      setVideoDuration(videoRef.current.duration);
    }
  };

  const handleClickOpen = (url: any) => {
    setVideoUrl(url);
    setOpen(true);
  };

  const handleClose = () => {
    setPause(true);
    setPlay(false);
    setOpen(false);
    setVideoUrl(null);
  };

  const handlePlay = () => {
    setPlay(false);
    setPause(true);
    if (videoRef.current) {
      videoRef.current.play();
    }
  };

  const handlePause = () => {
    setPlay(true);
    setPause(false);
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  const handleButtonClick = () => {
    fileInputRef?.current?.click();
  };

  useEffect(() => {
    let sCount = 0;
    let cCount = 0;
    let cLength: any = cameras?.length;
    for (let i = 0; i < cLength; i++) {
      if (
        cameras &&
        cameras[i]?.video_path &&
        cameras[i].status !== 'uploading'
      ) {
        sCount += 1;
        cCount += 1;
      }
    }
    if (sCount !== cCount) {
      refresh();
    }
  }, [rows, cameras]);

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Video Name',
      headerClassName: 'super-app-theme--header',
      flex: 1,
      renderCell: (params: GridCellParams) => {
        const record = params.row;
        return (
          record.status && (
            <div style={{ display: 'flex' }}>
              <EditName
                style={{
                  textDecoration:
                    record.status !== undefined &&
                    record.status !== 'uploading' &&
                    record.status !== 'failed' &&
                    record.url !== null
                      ? 'underline'
                      : 'none',
                  cursor: isRowEditing ? 'not-allowed' : 'pointer',
                  fontSize: '1vw',
                  fontWeight: 400,
                  margin: 0,
                  padding: 0
                }}
                type="camera"
                value={params.value}
                cameraId={record}
                isRowEditing={isRowEditing}
                setIsRowEditing={setIsRowEditing}
                handletextClick={() => {
                  if (isRowEditing) return;
                  if (
                    record.status !== undefined &&
                    record.status !== 'uploading' &&
                    record.status !== 'failed' &&
                    record.url !== null
                  ) {
                    refresh();
                    if (params.value) {
                      setVideoName(params.value.toString());
                    }
                    handleClickOpen(record.url);
                  }
                }}
              />
            </div>
          )
        );
      }
    },
    {
      field: 'duration',
      headerName: 'Duration',
      headerClassName: 'super-app-theme--header',
      flex: 0.6,
      renderCell: (params: GridCellParams) => {
        return (
          params.value && (
            <p className="text-[#102433] text-[1vw]">
              {params?.value?.toString()}
            </p>
          )
        );
      }
    },
    {
      field: 'date',
      headerName: 'Uploaded Date',
      headerClassName: 'super-app-theme--header',
      flex: 0.6,
      renderCell: (params: GridCellParams) => {
        return (
          params.value && (
            <p className="text-[#102433] text-[1vw]">
              {params?.value?.toString()}
            </p>
          )
        );
      }
    },
    {
      field: 'time',
      headerName: 'Uploaded Time',
      headerClassName: 'super-app-theme--header',
      flex: 0.6,
      renderCell: (params: GridCellParams) => {
        return (
          params.value && (
            <p className="text-[#102433] text-[1vw]">
              {params?.value?.toString()}
            </p>
          )
        );
      }
    },
    {
      field: 'status',
      headerName: 'Status',
      headerClassName: 'super-app-theme--header',
      flex: 0.4,
      filterable: false,
      disableReorder: true,
      disableColumnMenu: true,
      renderCell: (params: GridCellParams) => {
        const record = params.row;
        return (
          <VideoStatus
            status={params.value as string}
            uploadProgress={record.progress || 0}
            fail_reason={record.fail_reason as string}
            onCancelUpload={() => cancelUpload(record.id)}
          />
        );
      }
    },
    {
      field: 'action',
      headerName: 'Action',
      headerClassName: 'super-app-theme--header',
      flex: 0.4,
      disableReorder: true,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridCellParams) => {
        const record = params.row;

        return (
          <VideoAction
            status={record.status}
            onDelete={() => {
              handleDelete(record.id);
            }}
          />
        );
      }
    }
  ];

  const calculateProgress = (status: Status): number => {
    const totalItems = Object.keys(status.details).length;
    if (totalItems === 0) return 0;
    const trueCount = Object.values(status.details).filter(
      value => value === true
    ).length;
    return Math.round((trueCount / totalItems) * 100);
  };

  useEffect(() => {
    // Reset filters
    dispatch(resetFilterModel());
  }, []);

  return (
    <div className="w-full p-4 flex flex-col gap-2">
      <div className="flex w-full items-center justify-between">
        <h2 className="text-[1.1vw] font-[Proxima-Nova-Bold]">Train Videos</h2>
        <div className="flex flex-row items-center justify-end gap-3">
          <SearchSelect
            data={[
              ...(configs?.data.map((config: any) => ({
                label: config.name,
                value: config.id
              })) || [])
            ]}
            onChange={(value: any) => {
              dispatch(setConfiguration(value));
            }}
            disabled
            width="12vw"
            label="Select Configuration"
            selectValue={currentConfigId}
            placeholder="Configuration"
          />
          {currentConfigId !== 'Select Configuration' && (
            <button
              className="bg-[#102433] px-[3vw] py-[5px] text-white text-[1vw] rounded-sm flex items-center justify-start gap-2"
              onClick={handleButtonClick}
            >
              <Plus className="w-[1.2vw] h-auto" />
              Video
            </button>
          )}
          <input
            type="file"
            ref={fileInputRef}
            accept="video/*"
            multiple
            capture="user"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </div>
      </div>
      {cameras?.length ? (
        <div className="w-full">
          <style>
            {`
          .MuiDataGrid-columnHeader.MuiDataGrid-columnHeader--checkboxSelection {
            background-color: #102633 !important;
          }
          .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer {
            background-color: #102633 !important;
          }
          .MuiDataGrid-columnHeaderCheckbox .MuiSvgIcon-root {
            color: white !important;
          }
          .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root {
            color: white !important;
          }
        `}
          </style>
          <DataGridComponent
            columns={columns}
            disableRowSelectionOnClick={true}
            checkboxSelection
            isRowSelectable={params => params.row.status === 'uploaded'}
            rows={
              rows?.map((data: any) => {
                return {
                  id: data.id,
                  name: data?.name || '',
                  duration: secondsToHms(data?.video_metadata?.duration || 0),
                  date: moment
                    .utc(data?.created_at)
                    .local()
                    .format('MMM DD, YYYY'),
                  time: moment.utc(data?.created_at).local().format('hh:mm:ss'),
                  status: data?.status,
                  progress: data?.upload_progress || 0,
                  path: data?.video_path || '',
                  url: data?.video_url || ''
                };
              }) || []
            }
            loading={isLoading}
            height="70vh"
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
          />
        </div>
      ) : (
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '660px'
          }}
        >
          <div
            style={{
              borderStyle: 'dashed'
            }}
          >
            <IconButton
              style={{
                width: '20vw',
                padding: '20px',
                color: 'black'
              }}
              onClick={handleButtonClick}
            >
              {' '}
              <SystemUpdateAltIcon />
              <div style={{ marginLeft: '20px' }}>Import Videos</div>
            </IconButton>
          </div>
        </div>
      )}
      <div className="flex gap-[2vw] items-center justify-center w-full">
        <button
          className="flex justify-center items-center w-[10vw] py-[8px] text[#102433] text-[0.8vw] rounded-sm bg-[#ffffff] cursor-pointer border-[#102433] border-[1px]"
          onClick={() => navigate('/train')}
        >
          Back
        </button>
        <button
          className={`flex justify-center items-center w-[10vw] py-[8px] text-white text-[0.8vw] rounded-sm ${
            rows.some((row: any) => row.status === 'uploading')
              ? 'bg-gray-400 cursor-not-allowed' // Disabled state
              : 'bg-[#102433] cursor-pointer' // Enabled state
          }`}
          disabled={rows.some((row: any) => row.status === 'uploading')}
          onClick={async () => {
            if (selectedRows.length >= 3) {
              await getFrameGenerationResult().then((response: any) => {
                if (response?.status === 'success') {
                  const frameGenerationProgress = calculateProgress(
                    response?.data?.data
                  );
                  setProgress(frameGenerationProgress);
                }
                if (configurationData?.data?.config_type === 'pick_and_place') {
                  if (
                    response.status === 'success' &&
                    response?.data?.data?.frames_generated
                  ) {
                    dispatch(setAnnotationData([]));
                    dispatch(setObjectMapping([]));
                    dispatch(setWorkflowConfig([]));
                    navigate('/train/pick_&_place_annotation');
                  } else {
                    setOpenFramesProgress(true);
                  }
                } else {
                  dispatch(setCurrentCameraId(String(selectedRows[0])));
                  navigate('/train/activity_annotation');
                }
              });
            } else {
              Snack({
                message: 'Minimum 3 videos selection required to proceed.',
                severity: 'warning'
              });
            }
          }}
        >
          Proceed
        </button>
      </div>
      <Modal open={open} onClose={handleClose} size="xl">
        <DialogContent>
          {videoUrl && (
            <Stack>
              <Stack direction="row" alignItems="center" width="100%">
                <p>{videoName}</p>
              </Stack>
              <Stack sx={{ position: 'relative' }}>
                <video
                  ref={videoRef}
                  autoPlay
                  style={{ maxWidth: '100%', maxHeight: '70vh' }}
                  controls={false}
                  src={videoUrl}
                  onTimeUpdate={videoCurrentTime}
                  onLoadedMetadata={handleLoadedMetadata}
                />
                {loading && (
                  <CircularProgress
                    size={60}
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)'
                    }}
                  />
                )}
              </Stack>
              <Stack
                direction={'row'}
                alignItems={'center'}
                gap={2}
                marginTop={3}
              >
                {isPlay && (
                  <IconButton onClick={handlePlay}>
                    <PlayArrowIcon />
                  </IconButton>
                )}
                {isPause && (
                  <IconButton onClick={handlePause}>
                    <PauseIcon />
                  </IconButton>
                )}
                <Slider
                  value={getVideoCurrentTime}
                  max={videoDuration}
                  onChange={handleSliderChange}
                  aria-labelledby="continuous-slider"
                  style={{ width: '100%' }}
                />
              </Stack>
            </Stack>
          )}
        </DialogContent>
      </Modal>

      <Modal
        open={openFramesProgress}
        onClose={() => setOpenFramesProgress(false)}
        size="md"
      >
        <div className="w-full flex flex-col items-center gap-4">
          <OctagonAlertIcon height={40} width={40} color="#081319" />
          <h3 className="text-[1.2vw]">Please wait!</h3>
          <p className="text-[1vw]">
            Frame Generation in Progress! Check back in sometime.
          </p>
          <BorderLinearProgress variant="determinate" value={progress} />
          <button
            className="bg-[#102433] flex justify-center items-center w-[14vw] py-[8px] text-white text-[0.8vw] rounded-sm"
            onClick={() => {
              setOpenFramesProgress(false);
            }}
          >
            OK
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default UploadTrainVideos;
